import { useEffect, type ReactElement, type ReactNode } from 'react';
import { createRoot, type Root, type RootOptions } from 'react-dom/client';

declare global {
   interface Window {
      __pending_react_mounts?: number;
   }
}

const NEVER_COMMITTED_TIMEOUT_MS = 45_000;

/**
 * The WebDriver harness can only wait on `document.readyState`, but React 18
 * commits asynchronously (and some entry points even await network calls
 * before rendering), so a page can look "loaded" while its components have no
 * event handlers yet. Mount call sites bump this counter when they schedule a
 * mount; a wrapper effect decrements it after React commits. The harness
 * treats the page as interactive only when the counter is back to zero.
 *
 * The returned settle function is idempotent, and a watchdog releases the
 * gate if a mount never commits (e.g. its tree threw during render): without
 * it, one broken component would turn every later element lookup into a 60s
 * timeout pointing nowhere. The original error still reaches window.onerror.
 * The watchdog can't tell a crashed mount from a merely slow one, so it sits
 * just under the harness's 60s lookup timeout: only genuinely dead mounts
 * trip it, and its breadcrumb still lands before the lookup times out.
 */
function trackScheduledMount(): () => void {
   window.__pending_react_mounts = (window.__pending_react_mounts || 0) + 1;
   let settled = false;
   const settle = () => {
      if (settled) return;
      settled = true;
      window.__pending_react_mounts = (window.__pending_react_mounts || 1) - 1;
   };
   window.setTimeout(() => {
      if (!settled) {
         console.error(
            'A tracked React mount never committed; releasing the readiness gate.' +
               ' The mount likely crashed: check earlier errors.'
         );
         settle();
      }
   }, NEVER_COMMITTED_TIMEOUT_MS);
   return settle;
}

function MountCommitSignal({
   onCommitted,
   children,
}: {
   onCommitted: () => void;
   children: ReactNode;
}) {
   useEffect(() => {
      onCommitted();
   }, [onCommitted]);
   return <>{children}</>;
}

function withMountCommitSignal(element: ReactNode, onCommitted: () => void): ReactElement {
   return <MountCommitSignal onCommitted={onCommitted}>{element}</MountCommitSignal>;
}

/**
 * Drop-in replacement for react-dom's createRoot whose render() reports every
 * mount to the readiness gate. This is the default way to create a root in
 * Carpenter (enforced by lint): a root created with the raw createRoot renders
 * invisibly to the gate, and WebDriver interacts with the page before the
 * component has committed.
 *
 * Entry points that await network calls before they can render use
 * renderTrackedAsync instead, so the gate covers the await too.
 */
export function createTrackedRoot(
   container: Element | DocumentFragment,
   options?: RootOptions
): Root {
   const root = createRoot(container, options);
   return {
      render: (children: ReactNode) => {
         root.render(withMountCommitSignal(children, trackScheduledMount()));
      },
      unmount: () => root.unmount(),
   };
}

/**
 * Track a mount whose element needs async work (e.g. awaiting GrowthBook)
 * before it can render. The counter is bumped synchronously, before the first
 * await, so the gate can't pass while the network call is outstanding; if
 * buildElement rejects, the mount settles before the error propagates instead
 * of stalling the gate until the watchdog.
 */
export async function renderTrackedAsync(
   container: Element | DocumentFragment,
   buildElement: () => Promise<ReactNode>
): Promise<void> {
   const settle = trackScheduledMount();
   let element: ReactNode;
   try {
      element = await buildElement();
   } catch (error) {
      settle();
      throw error;
   }
   createRoot(container).render(withMountCommitSignal(element, settle));
}

/**
 * Holds the readiness gate for as long as it stays mounted, and releases it on
 * unmount. Render it inside a Suspense fallback: a fallback is a real commit,
 * so the tracked root's own signal fires while the page still shows a
 * placeholder, and the harness starts clicking a component that has not
 * loaded. This hold and that signal are both effects of the same commit, so
 * the count is still above zero once the commit has flushed.
 *
 * Use it only where the fallback is a transition. On a fallback that is a
 * resting state, this holds the gate until the watchdog gives up.
 */
export function ReadinessGateHold({ children }: { children?: ReactNode }) {
   useEffect(() => trackScheduledMount(), []);
   return <>{children}</>;
}

/**
 * Escape hatch for roots that must not gate WebDriver readiness, e.g.
 * rendering into a detached element to produce an HTML string.
 */
export { createRoot as createUntrackedRoot, type Root } from 'react-dom/client';
